import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { combineLatest, startWith, map } from 'rxjs';
import { UserDetails } from '#models';
import { UserRole } from '#enums';
import { AuthService } from './auth/auth.service';

interface NavTreeItemType {
  roles: UserRole[];
  routerLink?: string;
  subItems?: NavTreeType[];
}
type NavTreeType = Record<string, NavTreeItemType>;

@Injectable({
  providedIn: 'root',
})
export class NavbarService {
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);

  private readonly navTree: NavTreeType = {
    startWorkDone: {
      routerLink: '/start-work-dones',
      roles: [UserRole.User],
    },
    category: {
      routerLink: '/categories',
      roles: [UserRole.User],
    },
    administration: {
      roles: [UserRole.Admin, UserRole.SysAdmin],
      subItems: [
        {
          businessUnits: { routerLink: '/admin/business-units', roles: [UserRole.Admin] },
          areas: { routerLink: '/admin/areas', roles: [UserRole.Admin] },
          errorReductionTools: { routerLink: '/admin/error-reduction-tools', roles: [UserRole.Admin] },
          nonBadgedPersons: { routerLink: '/admin/non-badged-persons', roles: [UserRole.SysAdmin] },
        },
      ],
    },
  };

  getNavigation() {
    return combineLatest([
      this.translateService.onLangChange.pipe(startWith(this.translateService.getCurrentLang())),
      this.authService.userDetails$.pipe(startWith(null)),
    ]).pipe(map(([_, userDetails]) => this._getNavigationItems(userDetails, this.navTree)));
  }
  private _getNavigationItems(userDetails: UserDetails | null, navTree: NavTreeType): MenuItem[] {
    if (!userDetails) {
      return [];
    }

    const menuItems: MenuItem[] = [];

    const nav = Object.entries(navTree);
    for (const [key, value] of nav) {
      const { roles = [], routerLink = '', subItems = [] } = value;
      if (this.authService.isAuthorized(roles)) {
        const item: MenuItem = {
          label: this.translateService.instant(this._parseNavItemLabel(key)),
          routerLink: routerLink,
        };

        for (const subItem of subItems) {
          item.items = this._getNavigationItems(userDetails, subItem);
        }

        menuItems.push(item);
      }
    }

    return menuItems;
  }
  private _parseNavItemLabel(key: string): string {
    return 'navigation.' + key;
  }
}
